import { useMemo } from 'react'

import ProgressStep from '@components/ProgressStep/ProgressStep'
import { MarketBasketStatus } from '@domain'
import { faHourglassStart, faTriangleExclamation } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Chip, Skeleton, Stack, Typography } from '@mui/material'
import { useMarketBasketContext } from '@views/MarketBaskets/MarketBaskets.hooks'

import { getStatusFromProgress, progressMap, statusMap } from './ProgressBar.utils'

export default function ProgressBar() {
	const { marketBasket, isLoading } = useMarketBasketContext()

	const processingProgress = useMemo(
		() => (marketBasket?.processingStatus ? progressMap[marketBasket.processingStatus] : 0),
		[marketBasket]
	)

	if (isLoading) return <Skeleton width="100%" height="5rem" />

	if (marketBasket?.status === MarketBasketStatus.RECALCULATING) {
		return (
			<Stack gap={1}>
				<ProgressStep step={1} name="Updating" status="working" />
				<Chip
					sx={{ width: 'fit-content' }}
					label="Processing"
					color="primary"
					icon={<FontAwesomeIcon icon={faHourglassStart} />}
				/>
			</Stack>
		)
	}

	return (
		<Stack gap={1}>
			{/* Progress Bars */}
			<Stack direction="row" gap={1}>
				{['Gathering', 'Matching', 'Discovering', 'Backfilling', 'Pricing', 'Finishing'].map(
					(step, index) => (
						<ProgressStep
							key={step}
							step={index + 1}
							name={step}
							status={getStatusFromProgress(processingProgress, index + 1)}
						/>
					)
				)}
			</Stack>
			<Stack
				direction="row"
				alignItems="center"
				gap={1}
				sx={{ width: 'fit-content', maxWidth: '100%' }}
			>
				{/* Status */}
				{marketBasket?.status && (
					<Chip
						label={statusMap[marketBasket.status].text}
						color={statusMap[marketBasket.status].color}
						icon={<FontAwesomeIcon icon={statusMap[marketBasket.status].icon} />}
					/>
				)}
				{marketBasket?.status === 'FAILED' && (
					<Typography
						variant="body1"
						sx={{
							whiteSpace: 'nowrap',
							overflow: 'hidden',
							textOverflow: 'ellipsis',
						}}
					>
						{marketBasket?.analysisError}
					</Typography>
				)}
				{/* Warning (only show if market basket has not failed) */}
				{marketBasket?.analysisWarning && marketBasket.status !== 'FAILED' && (
					<Chip
						label="Warning"
						color="warning"
						icon={<FontAwesomeIcon icon={faTriangleExclamation} />}
					/>
				)}
				{marketBasket?.analysisWarning && marketBasket.status !== 'FAILED' && (
					<Typography
						variant="body1"
						sx={{
							whiteSpace: 'nowrap',
							overflow: 'hidden',
							textOverflow: 'ellipsis',
						}}
					>
						{marketBasket.analysisWarning}
					</Typography>
				)}
			</Stack>
		</Stack>
	)
}
